import { useOutletContext, useParams } from 'react-router-dom'

import { EditItAssetDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import EditModal from './EditModal'

interface EditItAssetPopupProps {
  assetId?: string
  refreshAsset?: () => Promise<void>
  onClose?: () => void
}

const EditItAssetPopup: React.FC<EditItAssetPopupProps> = ({
  assetId: propAssetId,
  refreshAsset: propRefreshAsset,
  onClose,
}) => {
  const { assetId: urlAssetId } = useParams<{ assetId: string }>()

  // Try to get refreshAsset from context (when used in AssetDetailsPage)
  const context = useOutletContext<{
    asset: unknown
    assetId: string
    refreshAsset: () => Promise<void>
  } | null>()

  // Use assetId from props if provided, otherwise use from URL params
  const assetId = propAssetId || urlAssetId

  // Use refreshAsset from props if provided, otherwise try to get from context
  const refreshAsset = propRefreshAsset || context?.refreshAsset

  const { data, loading, error } = useQuery(EditItAssetDocument, {
    variables: { assetId: assetId || '' },
    skip: !assetId,
    fetchPolicy: 'network-only',
  })

  if (!assetId) {
    return null
  }

  if (loading) {
    return null
  }

  if (error || !data?.itManagement?.itAsset) {
    return <div>Asset not found</div>
  }

  const asset = data.itManagement.itAsset

  // Transform the asset data to match EditModal's expected types
  const transformedAsset = {
    id: asset.id,
    serialNumber: asset.serialNumber || undefined,
    label: asset.label || undefined,
    notes: asset.notes || undefined,
    status: asset.status || undefined,
    purchaseDate: asset.purchaseDate || undefined,
    purchasePriceCents: asset.purchasePriceCents || undefined,
    warrantyEndDate: asset.warrantyEndDate || undefined,
    currency: asset.currency || undefined,
    itAssetModel: asset.itAssetModel
      ? {
          id: asset.itAssetModel.id,
          name: asset.itAssetModel.name || undefined,
          brand: asset.itAssetModel.brand || undefined,
          typeName: asset.itAssetModel.typeName || undefined,
        }
      : undefined,
  }

  return <EditModal asset={transformedAsset} refreshAsset={refreshAsset} onClose={onClose} />
}

export default EditItAssetPopup
